import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  BodyMeasurementsRepository,
  MeasurementSeriesRange,
  SaveSessionInput,
  UpdateBodyMetricPatch,
} from '../../application/ports/body-measurements.repository';
import type {BodyMetric, BodyMetricId, BodyMetricWithEntryCount} from '../../domain/body-metric';
import {
  BodyMetricNotFoundError,
  InvalidDateRangeError,
  InvalidMeasurementInputError,
  MeasurementEntryNotFoundError,
  MeasurementSessionRejectedError,
  MetricNameConflictError,
  NetworkError,
  ServerError,
  UnauthorizedError,
  type BodyMeasurementsError,
} from '../../domain/errors';
import type {MeasurementEntry, MeasurementEntryId} from '../../domain/measurement-entry';
import type {MetricOverviewItem} from '../../domain/metric-overview-item';
import type {BodyMetricDtoToBodyMetricMapper} from '../mappers/body-metric-dto-to-body-metric.mapper';
import type {BodyMetricListItemDtoToBodyMetricWithEntryCountMapper} from '../mappers/body-metric-list-item-dto-to-body-metric-with-entry-count.mapper';
import type {MeasurementEntryDtoToMeasurementEntryMapper} from '../mappers/measurement-entry-dto-to-measurement-entry.mapper';
import type {OverviewItemDtoToMetricOverviewItemMapper} from '../mappers/overview-item-dto-to-metric-overview-item.mapper';

/** Which resource a call targets; decides what a 4xx status means. */
type ErrorContext = 'metric' | 'session' | 'series' | 'entry';

const HTTP_UNAUTHORIZED = 401;

const ERROR_BY_STATUS: Record<
  ErrorContext,
  Readonly<Partial<Record<number, () => BodyMeasurementsError>>>
> = {
  metric: {
    400: () => new InvalidMeasurementInputError(),
    404: () => new BodyMetricNotFoundError(),
    409: () => new MetricNameConflictError(),
  },
  session: {
    400: () => new InvalidMeasurementInputError(),
    404: () => new BodyMetricNotFoundError(),
    422: () => new MeasurementSessionRejectedError(),
  },
  series: {
    400: () => new InvalidDateRangeError(),
    404: () => new BodyMetricNotFoundError(),
  },
  entry: {
    400: () => new InvalidMeasurementInputError(),
    404: () => new MeasurementEntryNotFoundError(),
  },
};

export class RpcBodyMeasurementsRepository implements BodyMeasurementsRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly metricMapper: BodyMetricDtoToBodyMetricMapper,
    private readonly metricWithCountMapper: BodyMetricListItemDtoToBodyMetricWithEntryCountMapper,
    private readonly entryMapper: MeasurementEntryDtoToMeasurementEntryMapper,
    private readonly overviewMapper: OverviewItemDtoToMetricOverviewItemMapper,
  ) {}

  async listMetrics(): Promise<Result<readonly BodyMetricWithEntryCount[], BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(this.rpcClient['body-measurements'].metrics.$get());
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.metricWithCountMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'metric');
    }
  }

  async createMetric(name: string): Promise<Result<BodyMetric, BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].metrics.$post({json: {name}}),
      );
      return ok(this.metricMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'metric');
    }
  }

  async updateMetric(
    metricId: BodyMetricId,
    patch: UpdateBodyMetricPatch,
  ): Promise<Result<BodyMetric, BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].metrics[':bodyMetricId'].$patch({
          param: {bodyMetricId: metricId},
          json: patch,
        }),
      );
      return ok(this.metricMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'metric');
    }
  }

  async deleteMetric(metricId: BodyMetricId): Promise<Result<void, BodyMeasurementsError>> {
    try {
      await parseResponse(
        this.rpcClient['body-measurements'].metrics[':bodyMetricId'].$delete({
          param: {bodyMetricId: metricId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause, 'metric');
    }
  }

  async getOverview(): Promise<Result<readonly MetricOverviewItem[], BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(this.rpcClient['body-measurements'].overview.$get());
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.overviewMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'metric');
    }
  }

  async getSession(
    measuredOn: string,
  ): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].sessions.$get({query: {date: measuredOn}}),
      );
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.entryMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'session');
    }
  }

  async saveSession(
    input: SaveSessionInput,
  ): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].sessions.$post({
          json: {measuredOn: input.measuredOn, values: [...input.values]},
        }),
      );
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.entryMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'session');
    }
  }

  async getSeries(
    metricId: BodyMetricId,
    range: MeasurementSeriesRange,
  ): Promise<Result<readonly MeasurementEntry[], BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].metrics[':bodyMetricId'].series.$get({
          param: {bodyMetricId: metricId},
          query: {from: range.from, to: range.to},
        }),
      );
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.entryMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause, 'series');
    }
  }

  async updateEntry(
    entryId: MeasurementEntryId,
    valueCm: number,
  ): Promise<Result<MeasurementEntry, BodyMeasurementsError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['body-measurements'].entries[':bodyMeasurementEntryId'].$patch({
          param: {bodyMeasurementEntryId: entryId},
          json: {valueCm},
        }),
      );
      return ok(this.entryMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause, 'entry');
    }
  }

  async deleteEntry(entryId: MeasurementEntryId): Promise<Result<void, BodyMeasurementsError>> {
    try {
      await parseResponse(
        this.rpcClient['body-measurements'].entries[':bodyMeasurementEntryId'].$delete({
          param: {bodyMeasurementEntryId: entryId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause, 'entry');
    }
  }

  private mapError(cause: unknown, context: ErrorContext): Result<never, BodyMeasurementsError> {
    if (!(cause instanceof DetailedError)) {
      return err(new NetworkError());
    }
    if (cause.statusCode === HTTP_UNAUTHORIZED) {
      return err(new UnauthorizedError());
    }
    const factory = ERROR_BY_STATUS[context][cause.statusCode];
    return err(factory === undefined ? new ServerError() : factory());
  }
}
